type EmptyOptions = {
    treatZeroAsEmpty?: boolean;
    treatFalseAsEmpty?: boolean;
    treatEmptyStringAsEmpty?: boolean;
    removeEmptyArrayItems?: boolean;
    mutate?: boolean;
};

const defaultOptions: Required<EmptyOptions> = {
    treatZeroAsEmpty: true,
    treatFalseAsEmpty: true,
    treatEmptyStringAsEmpty: true,
    removeEmptyArrayItems: true,
    mutate: false,
};

export function nullifyIfEmpty<T>(
    input: T,
    options?: EmptyOptions
): T | null {

    const opts = {
        ...defaultOptions,
        ...options,
    };

    const visited = new WeakSet();

    return clean(input, opts, visited);
}

function clean(
    value: any,
    opts: Required<EmptyOptions>,
    visited: WeakSet<object>
): any {

    // null / undefined
    if (value == null)
        return null;

    // primitive checks
    if (isPrimitiveEmpty(value, opts))
        return null;

    // Date
    if (value instanceof Date) {

        return isNaN(value.getTime())
            ? null
            : value;
    }

    // Array
    if (Array.isArray(value)) {

        const arr = opts.mutate
            ? value
            : [...value];

        for (let i = arr.length - 1; i >= 0; i--) {

            arr[i] = clean(arr[i], opts, visited);

            if (opts.removeEmptyArrayItems && arr[i] == null) {
                arr.splice(i, 1);
            }
        }

        return arr.length === 0
            ? null
            : arr;
    }

    // Object
    if (typeof value === 'object') {

        // circular refs protection
        if (visited.has(value))
            return value;

        visited.add(value);

        const obj = opts.mutate
            ? value
            : { ...value };

        let allEmpty = true;

        for (const key of Object.keys(obj)) {

            obj[key] = clean(obj[key], opts, visited);

            if (obj[key] != null) {
                allEmpty = false;
            }
        }

        return allEmpty
            ? null
            : obj;
    }

    return value;
}

function isPrimitiveEmpty(
    value: any,
    opts: Required<EmptyOptions>
): boolean {

    // string
    if (
        opts.treatEmptyStringAsEmpty &&
        typeof value === 'string' &&
        value.trim() === ''
    ) {
        return true;
    }

    // number
    if (
        opts.treatZeroAsEmpty &&
        typeof value === 'number' &&
        value === 0
    ) {
        return true;
    }

    // boolean
    if (
        opts.treatFalseAsEmpty &&
        typeof value === 'boolean' &&
        value === false
    ) {
        return true;
    }

    return false;
}